import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal, message } from 'antd';
import {
  AppstoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { BuildStatus } from '@todesktop/shared';
import { returnSortedBuilds } from '../../../~reusables/util';
import { isTDBuilderApp } from '../../../~reusables/util/schemaChecks';
import { useStore, selectedApp } from '../../../store';
import { goToAdminLogin, newApp } from '../../../~reusables/actions';
import { signOut } from '../../../~reusables/firebase';
import { useTransitionNavigation } from '../../../~reusables/contexts/TransitionContext';
import styled from '../../../~reusables/contexts/ThemeContext';
import {
  ACCOUNT_SETTINGS_TABS,
  LEGACY_ACCOUNT_SETTINGS_TABS,
  PROFILE_SETTINGS_TABS,
  TEAM_SETTINGS_TABS,
  getAccountSectionLinks,
  getAppSectionLinks,
  getAppSettingsTabs,
  getAppTabs,
  getV2AppSettingsTabs,
  signingTabsFor,
} from './navModel';
import {
  DocsProduct,
  getDocGroups,
  getDocsScope,
  STATUS_PAGE_URL,
} from './docs';
import { CommandItem, filterCommands, groupCommands } from './commandSearch';
import { useDocsSearch } from './docsSearch';
import { NavAppIcon } from './AppIcon';
import { AppIconFallback } from './NavStyles';
import { useFeatureFlag } from '../../../~reusables/hooks/useFeatureFlag';
import { copyAgentPrompt } from '../../v2/agentPrompt';

const ACCOUNT_KEYWORDS: Partial<Record<string, string>> = {
  'access-tokens': 'api cli',
  'team-general': 'team settings name logo organization',
  'user-management': 'team members invite users',
  'billing': 'team plan subscription invoices',
};

/** Team tabs are labelled for their page, so name the team ones here. */
const TEAM_COMMAND_LABELS: Partial<Record<string, string>> = {
  'team-general': 'Team settings',
};

const BUILD_STATUS_LABELS: Record<BuildStatus, string> = {
  [BuildStatus.building]: 'Building',
  [BuildStatus.queued]: 'Queued',
  [BuildStatus.preparation]: 'Preparing',
  [BuildStatus.succeeded]: 'Succeeded',
  [BuildStatus.failed]: 'Failed',
  [BuildStatus.cancelled]: 'Cancelled',
};

const openExternal = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Which docs ⌘K should show. Inside an app, its product's docs; elsewhere,
 * the product the account's apps use, or both if they use both.
 */
const useDocsScope = (): DocsProduct | null => {
  const { pathname } = useLocation();
  const { apps, currentApp } = useStore((state) => ({
    apps: state.apps,
    currentApp: selectedApp(state),
  }));
  // The selected app lingers in the store on account pages, so check the URL.
  const openAppId = pathname.match(/^\/apps\/([^/]+)/)?.[1];
  const openApp = currentApp && currentApp.id === openAppId ? currentApp : null;
  return getDocsScope(apps, openApp);
};

const useCommandItems = (docsScope: DocsProduct | null): CommandItem[] => {
  const { apps, currentApp, user, builds, releasedBuildId } = useStore(
    (state) => ({
      apps: state.apps,
      currentApp: selectedApp(state),
      user: state.user,
      builds: state.buildState.builds,
      releasedBuildId: state.buildState.releasedBuildId,
    }),
  );
  const { navigateWithTransition } = useTransitionNavigation();
  const hasTeams = useFeatureFlag('accountTeams');
  const v2Apps = useFeatureFlag('v2Apps');
  const v2SmokeTests = useFeatureFlag('v2SmokeTests');

  return useMemo(() => {
    const items: CommandItem[] = [];
    const go = (to: string) => () => navigateWithTransition(to);

    if (currentApp) {
      const appName = currentApp.name || 'This app';
      const tabs = getAppTabs(currentApp.id, {
        isBuilderApp: isTDBuilderApp(currentApp),
        isV2App: currentApp.appType === 'v2',
      }).filter((tab) => tab.key !== 'settings');
      for (const tab of tabs) {
        items.push({
          id: `page-${tab.key}`,
          group: 'Pages',
          label: tab.label,
          hint: appName,
          run: go(tab.to),
        });
      }
      const settingsTabs =
        currentApp.appType === 'v2'
          ? getV2AppSettingsTabs(currentApp.id, { smokeTests: v2SmokeTests })
          : getAppSettingsTabs(currentApp.id);
      for (const tab of settingsTabs) {
        items.push({
          id: `settings-${tab.key}`,
          group: 'Pages',
          label: `Settings: ${tab.label}`,
          hint: appName,
          keywords: tab.key === 'certificates' ? 'code signing' : undefined,
          run: go(tab.to),
        });
      }
      if (currentApp.appType === 'v2') {
        items.push({
          id: 'agent-setup-prompt',
          group: 'Actions',
          label: 'Copy the coding agent setup prompt',
          hint: appName,
          keywords: 'ai agent claude codex cursor integrate set up',
          run: () => {
            void copyAgentPrompt(currentApp.id).then(
              (copied) => copied && void message.success('Setup prompt copied'),
            );
          },
        });
      }
      // Cards within those pages are only listed once the user types. v2
      // apps' cards have no sections (searchable={false}).
      const sections =
        currentApp.appType === 'v2'
          ? []
          : getAppSectionLinks(currentApp.id, {
              isBuilderApp: isTDBuilderApp(currentApp),
              isElectron: currentApp.appType === 'electron',
              // Matches how the certificates page picks the app it shows.
              inheritsCertificates: apps.some(
                (app) => app.id === currentApp.parent?.id,
              ),
            });
      for (const section of sections) {
        items.push({
          id: `section-${section.key}`,
          group: 'Pages',
          label: section.label,
          hint: `${section.pageLabel} · ${appName}`,
          keywords: section.keywords,
          searchOnly: true,
          run: go(section.to),
        });
      }

      // Builds are only listed once the user types an id or version.
      if (!isTDBuilderApp(currentApp)) {
        for (const build of returnSortedBuilds(builds)) {
          const status =
            build.id === releasedBuildId
              ? 'Latest release'
              : BUILD_STATUS_LABELS[build.status] || build.status;
          items.push({
            id: `build-${build.id}`,
            group: 'Builds',
            label: build.appVersion ? `v${build.appVersion}` : build.id,
            hint: build.appVersion ? `${build.id} · ${status}` : status,
            keywords: `${build.id} ${build.appVersion || ''} build`,
            searchOnly: true,
            run: go(`/apps/${currentApp.id}/builds/${build.id}`),
          });
        }
      }
    }

    const sortedApps = [...apps].sort((a, b) => {
      if (a.id === currentApp?.id) return -1;
      if (b.id === currentApp?.id) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    for (const app of sortedApps) {
      items.push({
        id: `app-${app.id}`,
        group: 'Apps',
        label: app.name || 'Unnamed app',
        icon: <NavAppIcon app={app} />,
        hint: app.id === currentApp?.id ? 'Current app' : undefined,
        keywords: app.parent?.relationshipToParent,
        run: go(`/apps/${app.id}`),
      });
    }
    items.push(
      {
        id: 'all-apps',
        group: 'Apps',
        label: 'All apps',
        icon: (
          <AppIconFallback>
            <AppstoreOutlined />
          </AppIconFallback>
        ),
        keywords: 'home',
        run: go('/'),
      },
      {
        id: 'new-app',
        group: 'Apps',
        label: 'New app',
        icon: (
          <AppIconFallback>
            <PlusOutlined />
          </AppIconFallback>
        ),
        keywords: 'create add',
        run: () => newApp(),
      },
    );

    for (const tab of signingTabsFor(
      hasTeams
        ? [...PROFILE_SETTINGS_TABS, ...TEAM_SETTINGS_TABS]
        : LEGACY_ACCOUNT_SETTINGS_TABS,
      v2Apps,
    )) {
      items.push({
        id: `account-${tab.key}`,
        group: 'Account',
        label: TEAM_COMMAND_LABELS[tab.key] ?? tab.label,
        keywords: ACCOUNT_KEYWORDS[tab.key],
        run: go(tab.to),
      });
    }
    for (const section of getAccountSectionLinks(
      signingTabsFor(
        hasTeams ? ACCOUNT_SETTINGS_TABS : LEGACY_ACCOUNT_SETTINGS_TABS,
        v2Apps,
      ),
    )) {
      items.push({
        id: `account-section-${section.key}`,
        group: 'Account',
        label: section.label,
        hint: section.pageLabel,
        keywords: section.keywords,
        searchOnly: true,
        run: go(section.to),
      });
    }

    const docGroups = getDocGroups(apps, currentApp).filter(
      (group) => !docsScope || group.key === docsScope,
    );
    for (const group of docGroups) {
      for (const link of group.links) {
        items.push({
          id: `docs-${link.key}`,
          group: 'Help',
          label: link.label,
          hint: group.label,
          keywords: 'docs help',
          external: true,
          run: () => openExternal(link.url),
        });
      }
    }
    items.push({
      id: 'status',
      group: 'Help',
      label: 'System status',
      keywords: 'incident outage',
      external: true,
      run: () => openExternal(STATUS_PAGE_URL),
    });

    if (user?.isAdmin) {
      items.push(
        {
          id: 'admin-pulse',
          group: 'Admin',
          label: 'Pulse',
          run: go('/super-admin'),
        },
        {
          id: 'admin-customers',
          group: 'Admin',
          label: 'Customers',
          run: go('/super-admin/customers'),
        },
        {
          id: 'admin-inbox',
          group: 'Admin',
          label: 'Inbox',
          run: go('/super-admin/inbox'),
        },
        {
          id: 'admin-builds',
          group: 'Admin',
          label: 'Build health',
          run: go('/super-admin/builds'),
        },
        {
          id: 'admin-feature-flags',
          group: 'Admin',
          label: 'Feature flags',
          run: go('/super-admin/feature-flags'),
        },
        {
          id: 'admin-impersonate',
          group: 'Admin',
          label: 'Impersonate user',
          run: () => goToAdminLogin(),
        },
      );
    }

    items.push({
      id: 'logout',
      group: 'Account',
      label: 'Log out',
      keywords: 'sign out',
      run: () => signOut(),
    });

    return items;
  }, [
    apps,
    currentApp,
    user?.isAdmin,
    builds,
    releasedBuildId,
    navigateWithTransition,
    docsScope,
    hasTeams,
    v2Apps,
    v2SmokeTests,
  ]);
};

const DOCS_PRODUCT_NAMES: Record<DocsProduct, string> = {
  electron: 'Electron',
  builder: 'Builder',
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
}) => {
  const docsScope = useDocsScope();
  const items = useCommandItems(docsScope);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const docs = useDocsSearch(query, open, docsScope);
  const searchingDocsLabel = `Searching ${
    docsScope ? `${DOCS_PRODUCT_NAMES[docsScope]} ` : ''
  }docs…`;
  const results = useMemo(() => {
    const docItems: CommandItem[] = docs.results.map((result) => ({
      id: `docs-page-${result.url}`,
      group: 'Docs',
      label: result.title,
      description: result.excerpt,
      hint: `${result.product} docs`,
      external: true,
      run: () => openExternal(result.url),
    }));
    // Pagefind already ranked the docs; keep them after the local matches.
    return [...filterCommands(items, query), ...docItems];
  }, [items, query, docs.results]);
  const grouped = useMemo(() => groupCommands(results), [results]);
  // Grouping can reorder items, so navigate in the order they're displayed.
  const ordered = useMemo(
    () => grouped.flatMap((group) => group.items),
    [grouped],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const activeItem = ordered[activeIndex];

  useEffect(() => {
    if (!activeItem || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>(
      `[data-command-id="${activeItem.id}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeItem]);

  const run = (item: CommandItem | undefined) => {
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) =>
        ordered.length ? (index + 1) % ordered.length : 0,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) =>
        ordered.length ? (index - 1 + ordered.length) % ordered.length : 0,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(activeItem);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={600}
      style={{ top: 96 }}
      destroyOnHidden
      afterOpenChange={(isOpen) => {
        if (isOpen) inputRef.current?.focus();
      }}
      styles={{
        container: {
          padding: 0,
          overflow: 'hidden',
          background: 'var(--nav-bg)',
        },
      }}
      aria-label="Search"
    >
      <SearchField>
        <SearchOutlined
          style={{ color: 'var(--nav-text-subtle)', fontSize: 16 }}
        />
        <SearchInput
          ref={inputRef}
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search apps, builds, settings and docs"
          aria-label="Search apps, builds, settings and docs"
          role="combobox"
          aria-expanded
          aria-controls="command-palette-results"
          aria-activedescendant={
            activeItem ? `command-${activeItem.id}` : undefined
          }
          autoComplete="off"
          spellCheck={false}
        />
      </SearchField>
      <Results id="command-palette-results" role="listbox" ref={listRef}>
        {ordered.length === 0 && (
          <Empty>
            {docs.loading
              ? searchingDocsLabel
              : `No results for “${query.trim()}”`}
          </Empty>
        )}
        {grouped.map(({ group, items: groupItems }) => (
          <div key={group} role="group" aria-label={group}>
            <GroupLabel aria-hidden>{group}</GroupLabel>
            {groupItems.map((item) => {
              const index = ordered.indexOf(item);
              const active = index === activeIndex;
              return (
                <ResultOption
                  key={item.id}
                  id={`command-${item.id}`}
                  data-command-id={item.id}
                  role="option"
                  aria-selected={active}
                  $active={active}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => run(item)}
                >
                  {item.icon && (
                    <ResultIcon aria-hidden>{item.icon}</ResultIcon>
                  )}
                  <ResultText>
                    <span>{item.label}</span>
                    {item.description && (
                      <ResultDescription>{item.description}</ResultDescription>
                    )}
                  </ResultText>
                  {(item.hint || item.external) && (
                    <ResultHint>
                      {item.hint}
                      {item.external && ' ↗'}
                    </ResultHint>
                  )}
                </ResultOption>
              );
            })}
          </div>
        ))}
        {docs.loading && ordered.length > 0 && (
          <SearchingDocs aria-live="polite">{searchingDocsLabel}</SearchingDocs>
        )}
      </Results>
      <Footer>
        <span>
          <FooterKey>↑</FooterKey> <FooterKey>↓</FooterKey> to move
        </span>
        <span>
          <FooterKey>↵</FooterKey> to open
        </span>
        <span>
          <FooterKey>esc</FooterKey> to close
        </span>
      </Footer>
    </Modal>
  );
};

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--nav-border);
`;

const SearchInput = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 16px;
  color: var(--nav-text);

  &::placeholder {
    color: var(--nav-text-subtle);
  }
`;

const Results = styled.div`
  max-height: min(420px, 60vh);
  overflow-y: auto;
  padding: 6px 8px 8px;
`;

const GroupLabel = styled.div`
  padding: 10px 10px 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nav-text-subtle);
`;

const ResultOption = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--nav-text);
  background: ${(p) => (p.$active ? 'var(--nav-selected-bg)' : 'transparent')};
`;

const ResultIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  font-size: 12px;
`;

const ResultText = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ResultDescription = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: var(--nav-text-muted);
`;

const SearchingDocs = styled.div`
  padding: 8px 10px 4px;
  font-size: 12px;
  color: var(--nav-text-subtle);
`;

const ResultHint = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: var(--nav-text-muted);
  white-space: nowrap;
`;

const Empty = styled.div`
  padding: 24px 10px;
  text-align: center;
  font-size: 14px;
  color: var(--nav-text-muted);
`;

const Footer = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--nav-border);
  font-size: 12px;
  color: var(--nav-text-muted);
`;

const FooterKey = styled.kbd`
  padding: 0 5px;
  border: 1px solid var(--nav-input-border);
  border-bottom-width: 2px;
  border-radius: 4px;
  background: var(--nav-input-bg);
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
`;
